<script lang="ts" context="module">
    export type variants =
        | 'displayXLarge'
        | 'displayLarge'
        | 'displayMedium'
        | 'displaySmall'
        | 'headlineLarge'
        | 'headlineMedium'
        | 'headlineSmall'
        | 'titleLarge'
        | 'titleMedium'
        | 'titleSmall'
        | 'labelLarge'
        | 'labelMedium'
        | 'labelSmall'
        | 'bodyXLarge'
        | 'bodyLarge'
        | 'bodyMedium'
        | 'bodySmall';
</script>

<script lang="ts">
    import { theme } from '$theme/theme.svelte';
    const mapping = {
        displayXLarge: 'h1',
        displayLarge: 'h2',
        displayMedium: 'h3',
        displaySmall: 'h4',
        headlineLarge: 'h1',
        headlineMedium: 'h2',
        headlineSmall: 'h3',
        titleLarge: 'h1',
        titleMedium: 'h2',
        titleSmall: 'h3',
        labelLarge: 'span',
        labelMedium: 'span',
        labelSmall: 'span',
        bodyXLarge: 'p',
        bodyLarge: 'p',
        bodyMedium: 'p',
        bodySmall: 'p',
    };

    export let variant: variants = 'bodyMedium';
    export let component = '';
    export let italic = false;
    export let bold = false;
    export let underline = false;
    export let color: string = $theme.colors.primary[900];
    export let hoverColer = '';
    export let center = false;
    export let whiteSpace: string | null = null;

    let hovering = false;
    export let triggerHover = false;

    let computedColor: string = color;

    $: {
        if (hovering && hoverColer) {
            computedColor = hoverColer;
        } else if (triggerHover && hoverColer) {
            computedColor = hoverColer;
        } else {
            computedColor = color;
        }
    }

    let finalComponent = component || mapping[variant];
    $: finalComponent = component || mapping[variant];
</script>

<!-- svelte-ignore a11y-no-static-element-interactions -->
<svelte:element
    this={finalComponent}
    class:center-text={center}
    class:display-x-large={variant === 'displayXLarge'}
    class:display-large={variant === 'displayLarge'}
    class:display-medium={variant === 'displayMedium'}
    class:display-small={variant === 'displaySmall'}
    class:headline-large={variant === 'headlineLarge'}
    class:headline-medium={variant === 'headlineMedium'}
    class:headline-small={variant === 'headlineSmall'}
    class:title-large={variant === 'titleLarge'}
    class:title-medium={variant === 'titleMedium'}
    class:title-small={variant === 'titleSmall'}
    class:label-large={variant === 'labelLarge'}
    class:label-medium={variant === 'labelMedium'}
    class:label-small={variant === 'labelSmall'}
    class:body-x-large={variant === 'bodyXLarge'}
    class:body-large={variant === 'bodyLarge'}
    class:body-medium={variant === 'bodyMedium'}
    class:body-small={variant === 'bodySmall'}
    style:font-weight={bold
        ? variant.includes('display') || variant.includes('headline')
            ? 700
            : 600
        : 400}
    style:font-style={italic ? 'italic' : 'normal'}
    style:color={computedColor}
    style:text-decoration={underline ? 'underline' : 'none'}
    class:underline
    on:mouseover={() => (hovering = true)}
    on:mouseleave={() => (hovering = false)}
    on:focus={() => (hovering = true)}
    style:white-space={whiteSpace ? whiteSpace : ''}
    {...$$restProps}
>
    <slot {hovering} />
</svelte:element>

<style>
    .display-x-large {
        font-family: 'Raleway', sans-serif;
        font-size: 64px;
        line-height: 72px;
    }

    .display-large {
        font-family: 'Raleway', sans-serif;
        font-size: 57px;
        line-height: 64px;
    }

    .display-medium {
        font-family: 'Raleway', sans-serif;
        font-size: 45px;
        line-height: 52px;
    }

    .display-small {
        font-family: 'Raleway', sans-serif;
        font-size: 36px;
        line-height: 44px;
    }

    .headline-large {
        font-family: 'Raleway', sans-serif;
        font-size: 32px;
        line-height: 40px;
    }

    .headline-medium {
        font-family: 'Raleway', sans-serif;
        font-size: 28px;
        line-height: 36px;
    }

    .headline-small {
        font-family: 'Raleway', sans-serif;
        font-size: 24px;
        line-height: 32px;
    }

    .title-large {
        font-family: 'Inter', serif;
        font-size: 22px;
        line-height: 28px;
    }

    .title-medium {
        font-family: 'Inter', serif;
        font-size: 18px;
        line-height: 24px;
    }

    .title-small {
        font-family: 'Inter', serif;
        font-size: 14px;
        line-height: 20px;
    }

    .label-large {
        font-family: 'Inter', serif;
        font-size: 14px;
        line-height: 20px;
    }

    .label-medium {
        font-family: 'Inter', serif;
        font-size: 12px;
        line-height: 16px;
    }

    .label-small {
        font-family: 'Inter', serif;
        font-size: 11px;
        line-height: 16px;
    }

    .body-x-large {
        font-family: 'Inter', serif;
        font-size: 18px;
        line-height: 28px;
    }

    .body-large {
        font-family: 'Inter', serif;
        font-size: 16px;
        line-height: 26px;
    }

    .body-medium {
        font-family: 'Inter', serif;
        font-size: 14px;
        line-height: 20px;
    }

    .body-small {
        font-family: 'Inter', serif;
        font-size: 12px;
        line-height: 16px;
    }

    .center-text {
        text-align: center;
    }
</style>
